import React, { useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';

const AccessoryDebugger: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [accessoryName, setAccessoryName] = useState('Test Aksesuar');
  const [accessoryType, setAccessoryType] = useState('Düğme');
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

  const accessoryTypes = ['Düğme', 'Fermuar', 'Toka', 'Etiket', 'Astar', 'Diğer'];

  // Test log ekle
  const addLog = (message: string) => {
    const timestamp = new Date().toISOString().substring(11, 19); // Saat:Dakika:Saniye
    setTestResults(prev => [`[${timestamp}] ${message}`, ...prev]);
  };

  // JSON veri gönderme testi
  const testJsonPost = async () => {
    setLoading(true);
    addLog('JSON veri ile aksesuar oluşturma testi başlatılıyor...');
    
    try {
      const accessoryData = {
        name: accessoryName, // Bu değer doğrudan gönderilecek
        type: accessoryType,
        color: '#1E90FF',
        description: 'Test aksesuar açıklaması'
      };
      
      addLog(`Gönderilecek veri: ${JSON.stringify(accessoryData)}`);
      addLog(`Name değeri: "${accessoryData.name}"`);
      addLog(`API URL: ${apiUrl}/accessories`);
      
      // Doğrudan JSON formatında gönder
      const response = await axios({
        method: 'post',
        url: `${apiUrl}/accessories`,
        data: accessoryData,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      addLog('Başarılı yanıt alındı! Status: ' + response.status);
      addLog(`Oluşturulan aksesuar ID: ${response.data._id}`);
      addLog(`Yanıt içindeki name: ${response.data.name}`);
      addLog(`Tüm yanıt: ${JSON.stringify(response.data)}`);
    } catch (error: any) {
      addLog(`HATA: ${error.message}`);
      
      if (error.response) {
        addLog(`Hata Detayı: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // FormData ile veri gönderme testi
  const testFormDataPost = async () => {
    setLoading(true);
    addLog('FormData ile aksesuar oluşturma testi başlatılıyor...');
    
    try {
      const formData = new FormData();
      formData.append('name', accessoryName);
      formData.append('type', accessoryType);
      formData.append('color', '#32CD32');
      formData.append('description', 'Test FormData açıklaması');
      
      addLog('FormData oluşturuldu');
      addLog(`API URL: ${apiUrl}/accessories`);
      
      const response = await axios.post(`${apiUrl}/accessories`, formData);
      
      addLog('Başarılı yanıt alındı! Status: ' + response.status);
      addLog(`Oluşturulan aksesuar ID: ${response.data._id}`);
      addLog(`Yanıt: ${JSON.stringify(response.data)}`);
    } catch (error: any) {
      addLog(`HATA: ${error.message}`);
      
      if (error.response) {
        addLog(`Hata Detayı: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Doğrudan axios kullanarak test
  const testDirectAxiosPost = async () => {
    setLoading(true);
    addLog('Doğrudan axios ile aksesuar testi başlatılıyor...');
    
    try {
      const accessoryData = {
        name: accessoryName,
        type: accessoryType,
        color: '#9932CC',
        description: 'Test doğrudan axios açıklaması'
      };
      
      // ApiConfig yardımcı fonksiyonunu kullanma
      const url = getApiUrl('accessories');
      addLog(`API URL (getApiUrl ile): ${url}`);
      
      const response = await axios({
        method: 'post',
        url: url,
        data: accessoryData,
        headers: { 'Content-Type': 'application/json' }
      });
      
      addLog('Başarılı yanıt alındı! Status: ' + response.status);
      addLog(`Oluşturulan aksesuar ID: ${response.data._id}`);
    } catch (error: any) {
      addLog(`HATA: ${error.message}`);
      
      if (error.response) {
        addLog(`Hata Detayı: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Aksesuar API Hata Ayıklama</h1>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Test için aksesuar adı:</label>
          <input 
            type="text"
            value={accessoryName}
            onChange={(e) => setAccessoryName(e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Aksesuar tipi:</label>
          <select 
            value={accessoryType}
            onChange={(e) => setAccessoryType(e.target.value)}
            className="w-full p-2 border rounded"
          >
            {accessoryTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-4">
          <button
            onClick={testJsonPost}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            JSON Veri Testi
          </button>
          
          <button
            onClick={testFormDataPost}
            disabled={loading}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
          >
            FormData Testi
          </button>
          
          <button
            onClick={testDirectAxiosPost}
            disabled={loading}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
          >
            Doğrudan Axios Testi
          </button>
        </div>
      </div>

      <div className="bg-gray-900 rounded-lg p-4">
        <h2 className="text-white font-semibold mb-2">Test Sonuçları:</h2>
        <div className="bg-black text-green-400 p-4 rounded font-mono text-sm h-96 overflow-y-auto">
          {testResults.length > 0 ? (
            testResults.map((result, index) => (
              <div key={index}>{result}</div>
            ))
          ) : (
            <div className="text-gray-500">Henüz test yapılmadı</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessoryDebugger;
